/**
 * Characters a string may not carry into the database or onto a page, defined once.
 *
 * Three places read text nobody at the studio typed — a post's body (`@fareast/rich-text`
 * `parseBody`), a post's plain fields (`schemas/blog.ts`), and a visitor's form answers
 * (`modules/forms/service.ts`) — and they answer differently on purpose: a body or a field
 * is **refused** with a 422 its editor can act on, while a visitor's lead is **scrubbed and
 * kept**, because the portal never drops a lead. What they share is which characters are
 * the trouble, and that is this file.
 *
 *   - **An unpaired surrogate** is not a character at all. Postgres refuses one in `jsonb`.
 *   - **A C0 control** other than tab, newline and carriage return is invisible, and NUL is
 *     refused by Postgres in `text` and in `jsonb` alike — either reached the database as a
 *     500 rather than as an answer.
 *   - **U+2028 and U+2029**, the line and paragraph separators, are line breaks no editor
 *     types and a reader's tools disagree about.
 */
/** Half a surrogate pair without its other half. */
export const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/g;
/** C0 controls other than tab (U+0009), newline (U+000A) and carriage return (U+000D). */
// eslint-disable-next-line no-control-regex -- the control characters are the point
export const STRAY_CONTROLS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g;
/**
 * The first character a single line of text may not hold, named `U+XXXX`, or null.
 * A single line — a title, an alt text, a tag — holds no control at all but tab, no
 * line or paragraph separator, and no unpaired surrogate.
 */
export function firstForbiddenInLine(value) {
    // eslint-disable-next-line no-control-regex -- the control characters are the point
    return firstMatch(value, /[\u0000-\u0008\u000a-\u001f\u2028\u2029]/);
}
/**
 * The first character a multi-line text may not hold, named `U+XXXX`, or null: what
 * `firstForbiddenInLine` forbids, less the newline. A carriage return is forbidden here
 * too — a body's renderer knows where a newline may go and nothing about a `\r`.
 */
export function firstForbiddenInText(value) {
    // eslint-disable-next-line no-control-regex -- the control characters are the point
    return firstMatch(value, /[\u0000-\u0008\u000b-\u001f\u2028\u2029]/);
}
/** True when `value` is one line of text `firstForbiddenInLine` has nothing against. */
export function isPlainLine(value) {
    return firstForbiddenInLine(value) === null;
}
/**
 * Text somebody outside sent, made storable rather than refused: the stray controls
 * removed and an unpaired surrogate replaced with U+FFFD — what a UTF-8 decoder makes of
 * a broken character. Tab, newline and carriage return are kept: a visitor's message is
 * theirs, line breaks and all.
 */
export function scrubText(value) {
    return value.replace(STRAY_CONTROLS, '').replace(LONE_SURROGATE, '\ufffd');
}
/**
 * Text headed for a post's body, made storable rather than refused — what the editor
 * does to a paste or an import before `parseBody` ever sees it: every carriage return
 * (alone or before a newline) becomes a newline, the line and paragraph separators go,
 * and `scrubText` does the rest. A body's own rule (`firstForbiddenInText`) then has
 * nothing left to refuse.
 */
export function toBodyText(value) {
    return scrubText(value.replace(/\r\n?/g, '\n')).replace(/[\u2028\u2029]/g, '');
}
function firstMatch(value, controls) {
    const control = controls.exec(value);
    const surrogate = new RegExp(LONE_SURROGATE.source).exec(value);
    const first = control === null
        ? surrogate
        : surrogate === null || control.index < surrogate.index
            ? control
            : surrogate;
    if (first === null)
        return null;
    return `U+${first[0].charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}`;
}
//# sourceMappingURL=text.js.map